import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { authAPI } from '@/lib/api';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import {
  createNativeAuthPkce,
  NATIVE_AUTH_VERIFIER_KEY,
  resolveNativeAuthTarget,
  supportsNativeAppleAuth,
  supportsNativeGoogleAuth,
} from '@/lib/nativeAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowRight,
  Bell,
  Building2,
  Calendar,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  MapPin,
  Moon,
  ShieldCheck,
  Sparkles,
  Sun,
  Trophy,
  Users,
} from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const SPORTS = [
  { name: 'Calcetto', icon: '⚽', accent: 'from-emerald-400/20 to-emerald-950/20', border: 'border-emerald-400/30' },
  { name: 'Padel', icon: '🎾', accent: 'from-sky-400/20 to-sky-950/20', border: 'border-sky-400/30' },
  { name: 'Tennis', icon: '🎾', accent: 'from-amber-300/20 to-amber-950/20', border: 'border-amber-300/30' },
  { name: 'Beach Volley', icon: '🏐', accent: 'from-orange-400/20 to-orange-950/20', border: 'border-orange-400/30' },
];

const FEATURES = [
  {
    icon: Calendar,
    number: '01',
    title: 'Organizza in un attimo',
    description: 'Crea una partita, scegli campo e orario. Inviti, conferme e riserve restano insieme.'
  },
  {
    icon: Users,
    number: '02',
    title: 'Tieni insieme la squadra',
    description: 'Rosa, ruoli, livelli e disponibilità: ogni scelta è chiara prima di scendere in campo.'
  },
  {
    icon: Users,
    number: '03',
    title: 'Gioca, segui, migliora',
    description: 'Risultati, classifica e statistiche aggiornano la storia della tua lega partita dopo partita.'
  },
  {
    icon: CircleDollarSign,
    number: '04',
    title: 'Campi e quote sotto controllo',
    description: 'Trova strutture, gestisci le prenotazioni e non rincorrere più i pagamenti in chat.'
  },
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const authFormRef = React.useRef(null);
  const { 
    isAuthenticated, 
    loading, 
    user,
    facilityData,
    loginWithPassword, 
    registerWithEmail, 
    requestPhoneCode, 
    verifyPhoneCode,
    checkAuth,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [emailMode, setEmailMode] = React.useState('login'); // login | register
  const [emailForm, setEmailForm] = React.useState({ name: '', email: '', password: '' });
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [phoneStep, setPhoneStep] = React.useState('request'); // request | verify
  const [phoneForm, setPhoneForm] = React.useState({ name: '', email: '', phone: '', code: '' });
  const [phoneLoading, setPhoneLoading] = React.useState(false);
  const [appleLoading, setAppleLoading] = React.useState(false);
  const [debugCode, setDebugCode] = React.useState('');
  // Facility registration
  const [facilityForm, setFacilityForm] = React.useState({ name: '', email: '', password: '', phone: '' });
  const [facilityLoading, setFacilityLoading] = React.useState(false);
  const isNativeRuntime = Capacitor.isNativePlatform();
  const nativePlatform = Capacitor.getPlatform();
  const useNativeGoogleAuth = isNativeRuntime && supportsNativeGoogleAuth(nativePlatform);
  const useNativeAppleAuth = isNativeRuntime && supportsNativeAppleAuth(nativePlatform);

  // Pop a pending deep-link redirect saved by ProtectedRoute (or kept across OAuth round-trip).
  // Returns the path to redirect to, or null if no pending target.
  const consumePendingRedirect = () => {
    try {
      const target = sessionStorage.getItem('pendingRedirect');
      if (target) {
        sessionStorage.removeItem('pendingRedirect');
        return target;
      }
    } catch {}
    return null;
  };

  // Redirect if already authenticated
  React.useEffect(() => {
    if (!loading && isAuthenticated) {
      const pending = consumePendingRedirect();
      if (pending) {
        navigate(pending, { replace: true });
      } else if (user?.account_type === 'facility' && facilityData?.facility_id) {
        navigate(`/facilities/${facilityData.facility_id}/dashboard`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, loading, navigate, user, facilityData]);

  const handleLogin = () => {
    if (isNativeRuntime) {
      authFormRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Web Google OAuth via our backend.
    const target = encodeURIComponent(resolveNativeAuthTarget(sessionStorage.getItem('pendingRedirect')).split('#')[0]);
    window.location.href = `${getBackendBaseUrl()}/api/auth/google/start?redirect=${target}`;
  };

  const handleGoogleLogin = async () => {
    if (!useNativeGoogleAuth) return;
    try {
      const { verifier, challenge } = await createNativeAuthPkce();
      localStorage.setItem(NATIVE_AUTH_VERIFIER_KEY, verifier);
      const query = new URLSearchParams({
        channel: 'native',
        challenge,
        redirect: resolveNativeAuthTarget(sessionStorage.getItem('pendingRedirect')).split('#')[0],
      });
      await Browser.open({ url: `${getBackendBaseUrl()}/api/auth/google/start?${query}` });
    } catch (error) {
      localStorage.removeItem(NATIVE_AUTH_VERIFIER_KEY);
      console.error('Native Google OAuth start error:', error);
      toast.error('Impossibile avviare l’accesso con Google. Riprova.');
    }
  };

  const handleAppleLogin = async () => {
    if (!useNativeAppleAuth || appleLoading) return;
    setAppleLoading(true);
    try {
      const { verifier, challenge } = await createNativeAuthPkce();
      const { data: attempt } = await authAPI.startAppleAuth({
        challenge,
        target: resolveNativeAuthTarget(sessionStorage.getItem('pendingRedirect')).split('#')[0],
      });
      if (!attempt?.state || !attempt?.nonce) throw new Error('Tentativo Apple non valido');
      const { response } = await SignInWithApple.authorize({
        clientId: 'com.zingo.leaguehub',
        redirectURI: '',
        scopes: 'email name',
        state: attempt.state,
        nonce: attempt.nonce,
      });
      if (!response?.identityToken || !response?.authorizationCode) {
        throw new Error('Credenziali Apple incomplete');
      }

      const name = [response.givenName, response.familyName].filter(Boolean).join(' ').trim();
      const { data } = await authAPI.completeAppleAuth({
        state: attempt.state,
        verifier,
        identityToken: response.identityToken,
        authorizationCode: response.authorizationCode,
        ...(name && { name }),
      });
      if (!data?.token) throw new Error('Sessione Apple non valida');
      localStorage.setItem('leaguehub-token', data.token);
      const target = resolveNativeAuthTarget(consumePendingRedirect() || data.target);
      await checkAuth();
      navigate(target, { replace: true });
    } catch (error) {
      const message = String(error?.message || error || '');
      if (/1001|cancel/i.test(message)) toast.error('Accesso annullato.');
      else {
        console.error('Native Apple auth error:', error);
        toast.error(error.response?.data?.detail || 'Accesso con Apple non riuscito. Riprova.');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    try {
      if (emailMode === 'login') {
        const res = await loginWithPassword({ email: emailForm.email, password: emailForm.password });
        const pending = consumePendingRedirect();
        if (pending) {
          navigate(pending, { replace: true });
        } else if (res.user?.account_type === 'facility' && res.facility?.facility_id) {
          // Redirect facility accounts to their facility page
          navigate(`/facilities/${res.facility.facility_id}/dashboard`, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        await registerWithEmail({ name: emailForm.name, email: emailForm.email, password: emailForm.password });
        const pending = consumePendingRedirect();
        navigate(pending || '/dashboard', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore autenticazione');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleFacilityRegister = async (e) => {
    e.preventDefault();
    if (!facilityForm.name || !facilityForm.email || !facilityForm.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    setFacilityLoading(true);
    try {
      const res = await registerWithEmail({
        name: facilityForm.name,
        email: facilityForm.email,
        password: facilityForm.password,
        account_type: 'facility',
      });
      toast.success('Struttura registrata! Completa il tuo profilo.');
      if (res.facility?.facility_id) {
        navigate(`/facilities/${res.facility.facility_id}/edit`, { replace: true });
      } else {
        navigate('/facilities', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setFacilityLoading(false);
    }
  };

  const handleRequestPhoneCode = async (e) => {
    e.preventDefault();
    setPhoneLoading(true);
    setDebugCode('');
    try {
      const res = await requestPhoneCode({
        phone: phoneForm.phone,
        name: phoneForm.name,
        email: phoneForm.email || undefined
      });
      if (res?.debug_code) {
        setDebugCode(res.debug_code);
      }
      toast.success('Codice inviato');
      setPhoneStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio codice');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyPhoneCode = async (e) => {
    e.preventDefault();
    setPhoneLoading(true);
    try {
      await verifyPhoneCode({ phone: phoneForm.phone, code: phoneForm.code });
      navigate(consumePendingRedirect() || '/dashboard', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido');
    } finally {
      setPhoneLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex items-center gap-2">
          <img src="/logo.png" alt="LeagueHub" className="h-10 w-auto" />
          <span className="text-primary text-xl font-semibold">League<span className="text-foreground">Hub</span></span>
        </div>
      </div>
    );
  }

  return (
    <div className="safe-landing-page bg-[#f6f8fc] text-foreground dark:bg-background">
      {/* Header */}
      <header className="safe-landing-header fixed top-0 left-0 right-0 z-50 border-b border-slate-200/70 bg-[#f6f8fc]/90 backdrop-blur-xl dark:border-white/10 dark:bg-background/85">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5" aria-label="LeagueHub home">
            <div className="h-9 w-9 rounded-xl bg-slate-950 p-1.5 shadow-lg shadow-slate-950/15">
              <img src="/logo.png" alt="" className="h-full w-full object-contain" />
            </div>
            <span className="text-2xl font-bold tracking-tight leading-none">
              League<span className="text-primary">Hub</span>
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
            <a href="#come-funziona" className="hover:text-foreground transition-colors">Come funziona</a>
            <a href="#sport" className="hover:text-foreground transition-colors">Sport</a>
            <a href="#strutture" className="hover:text-foreground transition-colors">Per strutture</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'}
              data-testid="landing-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button onClick={handleLogin} className="rounded-xl px-4 shadow-lg shadow-primary/20" data-testid="login-button">
              Accedi
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="top" className="safe-landing-hero px-4 sm:px-6">
        <div className="max-w-7xl mx-auto relative overflow-hidden rounded-[2rem] bg-[#071d36] px-5 py-10 text-white shadow-[0_32px_80px_-32px_rgba(8,31,62,0.7)] sm:px-10 lg:px-14 lg:py-14">
          <div className="pointer-events-none absolute -top-32 right-[8%] h-80 w-80 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 left-[25%] h-96 w-96 rounded-full bg-blue-600/30 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.055) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />

          <div className="relative grid grid-cols-1 lg:grid-cols-[1.02fr_.98fr] gap-10 xl:gap-16 items-center">
            <div className="space-y-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-cyan-50 backdrop-blur">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-300" />
                </span>
                La tua prossima partita è già qui
              </div>

              <div className="space-y-5">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-200">Lo sport, senza frizioni</p>
                <h2 className="max-w-xl text-5xl sm:text-6xl lg:text-7xl font-bold leading-[.9] tracking-tight">
                  Meno chat.<br />
                  <span className="text-cyan-300">Più partite.</span>
                </h2>
                <p className="max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                  League Hub è il posto in cui la tua squadra organizza le partite, forma le rose, prenota il campo e segue la classifica. Tutto dall’app.
                </p>
              </div>

              <div className="flex flex-wrap gap-2.5">
                {SPORTS.map((sport) => (
                  <span
                    key={sport.name}
                    className="rounded-full border border-white/10 bg-white/8 px-3 py-1.5 text-sm font-medium text-slate-100 backdrop-blur"
                  >
                    {sport.icon} {sport.name}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="rounded-xl bg-cyan-300 px-6 text-base font-semibold text-slate-950 hover:bg-cyan-200"
                  onClick={handleLogin}
                  data-testid="hero-login-button"
                >
                  Inizia gratis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <a href="#come-funziona" className="inline-flex h-11 items-center justify-center rounded-xl border border-white/15 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                  Scopri come funziona
                </a>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-300">
                <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-cyan-300" /> Gratis per i giocatori</span>
                <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-cyan-300" /> Leghe, tornei e partite</span>
              </div>

              <Card ref={authFormRef} className="max-w-xl rounded-2xl border-white/10 bg-white text-slate-950 shadow-2xl shadow-black/20">
                <CardContent className="p-4 sm:p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold">Entra in League Hub</p>
                      <p className="text-xs text-slate-500">Accedi o crea il tuo account in pochi secondi.</p>
                    </div>
                    <ShieldCheck className="h-5 w-5 text-emerald-500" />
                  </div>
                  {isNativeRuntime && (
                    <div className="mb-4 space-y-3">
                      <div className={`grid gap-2 ${useNativeAppleAuth ? 'sm:grid-cols-2' : ''}`}>
                        {useNativeAppleAuth && (
                          <Button
                            type="button"
                            className="bg-black text-white hover:bg-black/90"
                            onClick={handleAppleLogin}
                            disabled={appleLoading}
                            aria-label="Continua con Apple"
                            data-testid="apple-login-button"
                          >
                            <span aria-hidden="true" className="mr-2 text-xl leading-none"></span>
                            {appleLoading ? 'Accesso...' : 'Continua con Apple'}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGoogleLogin}
                          data-testid="google-login-button"
                        >
                          Continua con Google
                        </Button>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span className="h-px flex-1 bg-slate-200" />
                        oppure
                        <span className="h-px flex-1 bg-slate-200" />
                      </div>
                    </div>
                  )}
                  <Tabs defaultValue="email">
                    <TabsList className="grid grid-cols-2 w-full">
                      <TabsTrigger value="email">Email</TabsTrigger>
                      <TabsTrigger value="phone">Telefono</TabsTrigger>
                    </TabsList>

                    <TabsContent value="email" className="mt-4">
                      <form onSubmit={handleEmailAuth} className="space-y-3">
                        {emailMode === 'register' && (
                          <div className="space-y-1">
                            <Label htmlFor="reg-name">Nome</Label>
                            <Input
                              id="reg-name"
                              placeholder="Il tuo nome"
                              value={emailForm.name}
                              onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })}
                              required={emailMode === 'register'}
                            />
                          </div>
                        )}
                        <div className="space-y-1">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="nome@email.it"
                            value={emailForm.email}
                            onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="password">Password</Label>
                          <Input
                            id="password"
                            type="password"
                            placeholder="Min 8 caratteri"
                            value={emailForm.password}
                            onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                            required
                          />
                        </div>

                        <div className="flex items-center justify-between gap-2">
                          <Button type="submit" disabled={emailLoading}>
                            {emailLoading ? 'Attendere...' : (emailMode === 'login' ? 'Accedi' : 'Registrati')}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setEmailMode(emailMode === 'login' ? 'register' : 'login')}
                          >
                            {emailMode === 'login' ? 'Crea account' : 'Hai già un account?'}
                          </Button>
                        </div>

                        {emailMode === 'login' && (
                          <button
                            type="button"
                            onClick={() => navigate('/forgot-password')}
                            className="text-sm text-blue-600 hover:underline self-start"
                          >
                            Password dimenticata?
                          </button>
                        )}
                      </form>
                    </TabsContent>

                    <TabsContent value="phone" className="mt-4">
                      {phoneStep === 'request' ? (
                        <form onSubmit={handleRequestPhoneCode} className="space-y-3">
                          <div className="space-y-1">
                            <Label htmlFor="phone-name">Nome</Label>
                            <Input
                              id="phone-name"
                              placeholder="Il tuo nome"
                              value={phoneForm.name}
                              onChange={(e) => setPhoneForm({ ...phoneForm, name: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="phone-email">Email (opzionale)</Label>
                            <Input
                              id="phone-email"
                              type="email"
                              placeholder="nome@email.it"
                              value={phoneForm.email}
                              onChange={(e) => setPhoneForm({ ...phoneForm, email: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="phone">Telefono</Label>
                            <Input
                              id="phone"
                              placeholder="+39 333 1234567"
                              value={phoneForm.phone}
                              onChange={(e) => setPhoneForm({ ...phoneForm, phone: e.target.value })}
                              required
                            />
                          </div>
                          <Button type="submit" disabled={phoneLoading}>
                            {phoneLoading ? 'Invio...' : 'Invia codice'}
                          </Button>
                          {debugCode && (
                            <p className="text-xs text-muted-foreground">
                              Codice dev: <code className="px-2 py-0.5 bg-muted rounded">{debugCode}</code>
                            </p>
                          )}
                        </form>
                      ) : (
                        <form onSubmit={handleVerifyPhoneCode} className="space-y-3">
                          <div className="space-y-1">
                            <Label htmlFor="otp">Codice ricevuto</Label>
                            <Input
                              id="otp"
                              placeholder="123456"
                              value={phoneForm.code}
                              onChange={(e) => setPhoneForm({ ...phoneForm, code: e.target.value })}
                              required
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Button type="submit" disabled={phoneLoading}>
                              {phoneLoading ? 'Verifica...' : 'Verifica e accedi'}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => setPhoneStep('request')}
                            >
                              Cambia numero
                            </Button>
                          </div>
                        </form>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>

            {/* LeagueHub product preview */}
            <div className="relative mx-auto w-full max-w-[510px] pt-3 lg:pt-0">
              <div className="absolute -inset-5 rounded-[2rem] bg-cyan-300/10 blur-2xl" />
              <div className="relative overflow-hidden rounded-[1.75rem] border border-white/15 bg-[#102c4c] p-3 shadow-2xl shadow-black/40">
                <div className="rounded-[1.35rem] bg-[#f7f9fc] p-4 text-slate-950 shadow-inner sm:p-5">
                  <div className="mb-5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-950 text-sm">⚽</div>
                      <div>
                        <p className="text-sm font-bold leading-none">Lega del giovedì</p>
                        <p className="mt-1 text-[11px] text-slate-500">Stagione 2026</p>
                      </div>
                    </div>
                    <div className="relative rounded-xl bg-slate-100 p-2">
                      <Bell className="h-4 w-4 text-slate-600" />
                      <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-orange-500" />
                    </div>
                  </div>

                  <div className="rounded-2xl bg-[#102c4c] p-4 text-white">
                    <div className="flex items-center justify-between text-[11px] font-medium text-cyan-200">
                      <span className="inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> Oggi · 21:30</span>
                      <span className="rounded-full bg-white/10 px-2 py-1">LIVE ROSA</span>
                    </div>
                    <div className="my-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-center">
                      <div><p className="text-sm font-semibold">Real Spritz</p><p className="mt-1 text-3xl font-bold">3</p></div>
                      <span className="rounded-full border border-white/15 px-2 py-1 text-xs text-slate-300">VS</span>
                      <div><p className="text-sm font-semibold">FC Spacca</p><p className="mt-1 text-3xl font-bold">3</p></div>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-3 text-xs text-slate-300">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-cyan-300" /> Sport Village</span>
                      <span className="font-semibold text-cyan-200">Apri partita →</span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-[1.25fr_.75fr] gap-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <div className="mb-3 flex items-center justify-between"><p className="text-xs font-bold">Rosa confermata</p><span className="text-xs font-semibold text-emerald-600">8/10</span></div>
                      <div className="flex -space-x-2">
                        {['A', 'M', 'L', 'G', 'F'].map((initial, index) => (
                          <span key={initial} className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white ${['bg-sky-500', 'bg-violet-500', 'bg-orange-500', 'bg-emerald-500', 'bg-rose-500'][index]}`}>{initial}</span>
                        ))}
                        <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-slate-100 text-[10px] font-bold text-slate-500">+3</span>
                      </div>
                    </div>
                    <div className="rounded-2xl bg-cyan-100 p-3">
                      <Trophy className="h-4 w-4 text-cyan-700" />
                      <p className="mt-2 text-xl font-bold leading-none text-cyan-950">#2</p>
                      <p className="mt-1 text-[10px] font-medium text-cyan-800">in classifica</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -right-3 top-[21%] hidden rounded-2xl border border-white/15 bg-white/95 p-3 text-slate-950 shadow-xl xl:block">
                <div className="flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100"><Check className="h-4 w-4 text-emerald-600" /></span><div><p className="text-xs font-bold">Quota pagata</p><p className="text-[10px] text-slate-500">Manca solo giocare</p></div></div>
              </div>
              <div className="absolute -bottom-5 -left-8 hidden rounded-2xl border border-white/15 bg-white/95 p-3 text-slate-950 shadow-xl xl:block">
                <div className="flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-100"><Users className="h-4 w-4 text-orange-600" /></span><div><p className="text-xs font-bold">2 riserve</p><p className="text-[10px] text-slate-500">Pronte a entrare</p></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product value */}
      <section id="come-funziona" className="px-4 pb-20 pt-24 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 grid gap-5 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"><Sparkles className="h-3.5 w-3.5" /> Pensato per chi gioca davvero</div>
              <h3 className="text-4xl sm:text-5xl font-bold leading-[.95]">La tua lega,<br /><span className="text-primary">in un unico ritmo.</span></h3>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">Ogni dettaglio che oggi sparisce in una chat trova il suo posto: chi c’è, dove si gioca, quanto manca, chi vince e cosa succede dopo.</p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((feature) => (
              <Card key={feature.title} className="group rounded-2xl border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl dark:border-white/10 dark:bg-card">
                <CardContent className="p-5">
                  <div className="mb-8 flex items-start justify-between">
                    <span className="text-xs font-bold tracking-widest text-slate-400">{feature.number}</span>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-950 text-white transition-transform group-hover:rotate-6"><feature.icon className="h-5 w-5" /></span>
                  </div>
                  <h4 className="text-xl font-bold leading-none">{feature.title}</h4>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-5 grid overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[.95fr_1.05fr] dark:border-white/10 dark:bg-card">
            <div className="bg-slate-950 p-7 text-white sm:p-9">
              <p className="text-xs font-semibold uppercase tracking-[.18em] text-cyan-300">Una schermata, tutte le risposte</p>
              <h4 className="mt-4 max-w-md text-3xl sm:text-4xl font-bold leading-[.95]">Non devi più chiedere: “chi c’è?”</h4>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-300">League Hub rende chiaro lo stato della partita prima che inizi: titolari, riserve, quota, luogo e notifiche.</p>
              <div className="mt-8 space-y-3 text-sm">
                {['Presenze aggiornate in tempo reale', 'Squadre bilanciate per skill', 'Risultati e statistiche nello stesso posto'].map(item => (
                  <div key={item} className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-300 text-slate-950"><Check className="h-3 w-3" /></span>{item}</div>
                ))}
              </div>
            </div>
            <div className="p-5 sm:p-8">
              <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-[#f8fafc] p-4 shadow-xl dark:border-white/10 dark:bg-muted/20">
                <div className="mb-4 flex items-center justify-between"><div><p className="text-xs font-semibold text-slate-500">GIOVEDÌ · 21:30</p><p className="mt-1 font-bold">Calcetto Cavernago</p></div><span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">8 confermati</span></div>
                <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-card"><div className="flex items-center justify-between text-sm"><span className="font-semibold">Squadra A</span><span className="font-bold text-primary">18 pt skill</span></div><div className="my-3 h-px bg-slate-100 dark:bg-white/10" /><div className="flex items-center justify-between text-sm"><span className="font-semibold">Squadra B</span><span className="font-bold text-primary">18 pt skill</span></div></div>
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-cyan-50 px-3 py-2 text-xs font-medium text-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100"><MapPin className="h-4 w-4 text-cyan-600" /> Campo Sport Village · 2,1 km</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sports */}
      <section id="sport" className="px-4 pb-20 sm:px-6">
        <div className="max-w-7xl mx-auto rounded-3xl bg-[#102c4c] px-6 py-10 text-white sm:px-10 lg:py-14">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <div><p className="text-xs font-semibold uppercase tracking-[.18em] text-cyan-300">Un solo hub, tanti modi di giocare</p><h3 className="mt-3 text-4xl sm:text-5xl font-bold leading-[.95]">Ogni sport merita<br />la sua squadra.</h3></div>
            <p className="max-w-sm text-sm leading-relaxed text-slate-300">Crea partite, leghe e tornei secondo il tuo gioco. Il sistema resta semplice anche quando cambiano campo e regole.</p>
          </div>
          <div className="mt-9 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {SPORTS.map(sport => (
              <div key={sport.name} className={`group rounded-2xl border ${sport.border} bg-gradient-to-br ${sport.accent} p-4 transition-transform hover:-translate-y-1`}>
                <span className="text-3xl">{sport.icon}</span><p className="mt-7 text-lg font-bold">{sport.name}</p><p className="mt-1 text-xs text-slate-300">Leghe · partite · squadre</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facility Registration Section */}
      <section id="strutture" className="px-4 py-20 sm:px-6">
        <div className="max-w-7xl mx-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10 dark:border-white/10 dark:bg-card">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-primary font-semibold text-xs">
                <Building2 className="h-4 w-4" />
                Per le strutture sportive
              </div>
              <h3 className="text-4xl sm:text-5xl font-bold leading-[.95]">
                Trasforma i tuoi campi in <span className="text-primary">partite piene.</span>
              </h3>
              <p className="text-lg text-muted-foreground">
                Dai visibilità alla tua struttura, gestisci disponibilità e prenotazioni e lasci che League Hub porti il gioco fino al tuo campo.
              </p>
              <ul className="space-y-3">
                {[
                  'Profilo struttura con foto, contatti e servizi',
                  'Gestione campi con sport, tariffe e disponibilità',
                  'Sistema prenotazioni con calendario integrato',
                  'Visibilità nelle partite organizzate dai giocatori',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0"><Check className="h-3 w-3" /></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <Card className="rounded-2xl border-primary/15 bg-[#071d36] text-white shadow-2xl shadow-primary/15">
              <CardContent className="p-6 space-y-1 sm:p-7">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-300 text-slate-950"><Building2 className="h-5 w-5" /></span>
                  <div><h4 className="text-xl font-bold">Porta la tua struttura su League Hub</h4><p className="text-xs text-slate-300">Crea il tuo profilo gratuito.</p></div>
                </div>
                <form onSubmit={handleFacilityRegister} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="fac-name" className="text-slate-200">Nome Struttura *</Label>
                    <Input
                      id="fac-name"
                      placeholder="Es: Centro Sportivo Roma Nord"
                      className="border-white/10 bg-white/10 text-white placeholder:text-slate-400"
                      value={facilityForm.name}
                      onChange={(e) => setFacilityForm({ ...facilityForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fac-email" className="text-slate-200">Email *</Label>
                    <Input
                      id="fac-email"
                      type="email"
                      placeholder="info@tuastruttura.it"
                      className="border-white/10 bg-white/10 text-white placeholder:text-slate-400"
                      value={facilityForm.email}
                      onChange={(e) => setFacilityForm({ ...facilityForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fac-password" className="text-slate-200">Password *</Label>
                    <Input
                      id="fac-password"
                      type="password"
                      placeholder="Min 8 caratteri"
                      className="border-white/10 bg-white/10 text-white placeholder:text-slate-400"
                      value={facilityForm.password}
                      onChange={(e) => setFacilityForm({ ...facilityForm, password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fac-phone" className="text-slate-200">Telefono (opzionale)</Label>
                    <Input
                      id="fac-phone"
                      placeholder="+39 06 1234567"
                      className="border-white/10 bg-white/10 text-white placeholder:text-slate-400"
                      value={facilityForm.phone}
                      onChange={(e) => setFacilityForm({ ...facilityForm, phone: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-cyan-300 font-semibold text-slate-950 hover:bg-cyan-200" size="lg" disabled={facilityLoading}>
                    {facilityLoading ? 'Registrazione...' : 'Registra Struttura'}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="pt-1 text-center text-xs text-slate-300">
                    Hai già un account struttura?{' '}
                    <button type="button" className="text-cyan-300 hover:underline" onClick={() => {
                      setEmailMode('login');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}>
                      Accedi qui
                    </button>
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 pb-20 sm:px-6">
        <div className="max-w-7xl mx-auto relative overflow-hidden rounded-3xl bg-primary px-6 py-12 text-white sm:px-12 sm:py-16">
          <div className="absolute -right-20 -top-28 h-72 w-72 rounded-full border-[28px] border-white/10" />
          <div className="absolute -bottom-44 left-[12%] h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="relative max-w-2xl space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold"><Sparkles className="h-3.5 w-3.5" /> Il campo è già pronto</div>
            <h3 className="text-4xl sm:text-6xl font-bold leading-[.9]">La prossima partita<br />inizia da qui.</h3>
            <p className="max-w-xl text-base leading-relaxed text-blue-100 sm:text-lg">Crea il tuo account, entra in una lega e rimetti il gioco al centro. League Hub è gratis per tutti i giocatori.</p>
            <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              className="px-6 bg-white text-primary hover:bg-white/95 font-bold shadow-lg"
              onClick={handleLogin}
              data-testid="cta-login-button"
            >
              {isNativeRuntime ? 'Accedi o registrati' : 'Inizia con Google'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
              onClick={() => navigate('/business')}
            >
              <Building2 className="h-5 w-5 mr-2" />
              Sei una struttura?
            </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 px-4 py-8 sm:px-6 dark:border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2"><img src="/logo.png" alt="" className="h-6 w-6" /><p className="text-sm text-muted-foreground">© 2026 LeagueHub. Il tuo sport, organizzato.</p></div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy</a>
            <a href="/account-deletion" className="hover:text-foreground transition-colors">Cancella account</a>
            <a href="mailto:supporto@leaguehub.it" className="hover:text-foreground transition-colors">Contatti</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
